import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { BookOpen, ShoppingBag, Download, Loader2, CreditCard, Menu, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const emailIsValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const Store = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [email, setEmail] = useState("");
  const [stripeLoading, setStripeLoading] = useState(false);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/store/products`);
      setProducts(res.data);
    } catch (e) {
      toast.error("Failed to load store products");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const openBuy = (product) => {
    setSelectedProduct(product);
    setEmail("");
  };

  const handleStripeCheckout = async () => {
    if (!emailIsValid(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setStripeLoading(true);
    try {
      const res = await axios.post(`${API}/store/checkout`, {
        product_id: selectedProduct.id,
        customer_email: email,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
    } finally {
      setStripeLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button
              onClick={() => navigate("/")}
              className="text-2xl font-bold text-white hover:text-white/90 transition-colors cursor-pointer"
              style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.8)" }}
              data-testid="logo-home-link"
            >
              LeProfBerni
            </button>
            <div className="hidden md:flex space-x-8 items-center">
              <button onClick={() => navigate("/")} className="text-white hover:text-white/80 transition-colors" style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.8)" }} data-testid="nav-home">Home</button>
              <button onClick={() => navigate("/about")} className="text-white hover:text-white/80 transition-colors" style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.8)" }} data-testid="nav-about">About</button>
              <button onClick={() => navigate("/contact")} className="text-white hover:text-white/80 transition-colors" style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.8)" }} data-testid="nav-contact">Contact</button>
              <span className="text-white font-semibold flex items-center gap-1" style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.8)" }} data-testid="nav-store-active"><ShoppingBag className="w-4 h-4" /> Store</span>
            </div>
            <button className="md:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-testid="mobile-menu-button">
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#04539F] border-t border-white/20" data-testid="mobile-menu">
            <div className="px-4 py-6 space-y-4">
              <button onClick={() => navigate("/")} className="block w-full text-left text-white py-2" data-testid="mobile-nav-home">Home</button>
              <button onClick={() => navigate("/about")} className="block w-full text-left text-white py-2" data-testid="mobile-nav-about">About</button>
              <button onClick={() => navigate("/contact")} className="block w-full text-left text-white py-2" data-testid="mobile-nav-contact">Contact</button>
            </div>
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="pt-32 pb-10 px-4 sm:px-6 lg:px-8 text-center" data-testid="store-header">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white" data-testid="store-title">
          Learning Store
        </h1>
        <p className="text-lg text-white/90 mt-4 max-w-2xl mx-auto">
          Downloadable ebooks, worksheets and resources to boost your language journey. Instant access after payment.
        </p>
      </section>

      {/* Products */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 text-white animate-spin" data-testid="store-loading" />
            </div>
          ) : products.length === 0 ? (
            <Card className="max-w-lg mx-auto" data-testid="store-empty">
              <CardContent className="py-16 text-center">
                <ShoppingBag className="w-12 h-12 mx-auto text-[#04539F] mb-4" />
                <p className="text-lg font-semibold text-gray-900">No products available yet</p>
                <p className="text-gray-600 mt-2">Check back soon for new learning materials!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
              {products.map((product) => (
                <Card key={product.id} className="overflow-hidden hover:shadow-2xl transition-shadow bg-white" data-testid={`product-card-${product.id}`}>
                  <div className="h-56 bg-gradient-to-br from-[#04539F] to-[#0881F0] flex items-center justify-center">
                    {product.has_cover ? (
                      <img
                        src={`${API}/store/products/${product.id}/cover`}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        data-testid={`product-cover-${product.id}`}
                      />
                    ) : (
                      <BookOpen className="w-20 h-20 text-white/80" />
                    )}
                  </div>
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-xl font-bold text-gray-900" data-testid={`product-name-${product.id}`}>{product.name}</h3>
                    <p className="text-gray-600 text-sm line-clamp-3" data-testid={`product-desc-${product.id}`}>{product.description}</p>
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-2xl font-bold text-[#04539F]" data-testid={`product-price-${product.id}`}>€{product.price.toFixed(2)}</span>
                      <Button
                        className="bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full px-6"
                        onClick={() => openBuy(product)}
                        data-testid={`buy-btn-${product.id}`}
                      >
                        Buy Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Purchase Dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => !open && setSelectedProduct(null)}>
        <DialogContent className="max-w-md" data-testid="purchase-dialog">
          <DialogHeader>
            <DialogTitle>Complete your purchase</DialogTitle>
            <DialogDescription>
              {selectedProduct && `${selectedProduct.name} — €${selectedProduct.price.toFixed(2)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="buyer-email" className="text-sm font-medium">Your email (for delivery)</label>
              <Input
                id="buyer-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="buyer-email-input"
              />
            </div>

            <Button
              className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full flex items-center justify-center gap-2"
              onClick={handleStripeCheckout}
              disabled={stripeLoading}
              data-testid="pay-stripe-btn"
            >
              {stripeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Pay with Card
            </Button>

            <div className="flex items-center gap-3 text-xs text-gray-400">
              <div className="flex-1 h-px bg-gray-200" /> OR <div className="flex-1 h-px bg-gray-200" />
            </div>

            {selectedProduct && PAYPAL_CLIENT_ID && (
              <div data-testid="paypal-button-wrapper">
                <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR", intent: "capture" }}>
                  <PayPalButtons
                    style={{ layout: "horizontal", color: "blue", shape: "pill", tagline: false }}
                    disabled={!emailIsValid(email)}
                    forceReRender={[selectedProduct?.id, email]}
                    createOrder={async () => {
                      if (!emailIsValid(email)) {
                        toast.error("Please enter a valid email first");
                        throw new Error("invalid email");
                      }
                      const res = await axios.post(`${API}/store/paypal/create-order`, {
                        product_id: selectedProduct.id,
                        customer_email: email,
                        origin_url: window.location.origin,
                      });
                      return res.data.order_id;
                    }}
                    onApprove={async (data) => {
                      try {
                        const res = await axios.post(`${API}/store/paypal/capture`, {
                          paypal_order_id: data.orderID,
                        });
                        if (res.data.payment_status === "paid" && res.data.download_token) {
                          navigate(`/store/success?token=${res.data.download_token}`);
                        } else {
                          toast.error("Payment not completed. Please try again.");
                        }
                      } catch (e) {
                        toast.error("Payment capture failed.");
                      }
                    }}
                    onError={() => toast.error("PayPal error. Please try again or use card.")}
                  />
                </PayPalScriptProvider>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Store;
